const GWP_PROPERTY = '_gwp';

const _debug =
  typeof URLSearchParams !== 'undefined' &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location?.search || '').has('debug_gwp_validator');

function log(...args: unknown[]) {
  if (_debug) console.debug('[GWP Validator]', ...args);
}

export function isGwpLine(
  item: { properties?: Record<string, unknown> } | null | undefined,
): boolean {
  return item?.properties?.[GWP_PROPERTY] === 'true';
}

// A GWP line is any cart line marked _gwp:"true" — price-agnostic, no variant gate.
// The discount function is the source of truth for eligibility: it applies a 100%
// product discount to up to maxFreeQuantity eligible gift units. This module trusts
// that output and enforces "every _gwp unit must be free": it caps each _gwp line to
// the number of units the function actually freed, removing the rest.
//
//   freeUnits = round( (sumLineLevelDiscount / original_line_price) * quantity )
//
// Correct whether or not Shopify splits a partial-quantity discount into separate lines
// (split: each line is binary; no-split: one line capped to its freed units). Both inputs
// are same-subunit integers, so the ratio is currency-agnostic. Uses line-level allocations
// only, so cart/order-level discounts never count toward "freed". Lines we cannot compute
// (malformed/missing fields) are left untouched rather than removed.
export function getCartUpdates(
  cart:
    | {
        items?: Array<{
          key?: string;
          quantity?: number;
          final_line_price?: unknown;
          original_line_price?: unknown;
          line_level_discount_allocations?: Array<{ amount?: unknown } | null>;
          properties?: Record<string, unknown>;
        }>;
      }
    | null
    | undefined,
): Record<string, number> {
  if (!cart?.items?.length) return {};

  const updates: Record<string, number> = {};

  for (const item of cart.items) {
    if (!isGwpLine(item)) continue;

    // Fast path: fully free (covers genuinely $0-priced gifts too) -> keep.
    if (item.final_line_price === 0) {
      log('keep (free):', item.key);
      continue;
    }

    const originalLineTotal = item.original_line_price;
    const quantity = item.quantity || 0;
    // freeUnits is computed from originalLineTotal/quantity/allocations only; the
    // final_line_price check is a data-trust gate — a malformed price means the line's
    // /cart.js shape is suspect, so skip it (leave untouched) rather than act on bad data.
    if (
      typeof originalLineTotal !== 'number' ||
      originalLineTotal <= 0 ||
      typeof item.final_line_price !== 'number' ||
      quantity <= 0
    ) {
      log('skip (cannot compute free units):', item.key);
      continue;
    }

    const discounted = (item.line_level_discount_allocations || []).reduce(
      (sum, a) => sum + (Number(a?.amount) || 0),
      0,
    );
    const freeUnits = Math.round((discounted / originalLineTotal) * quantity);

    if (freeUnits >= quantity) {
      log('keep (all units free):', item.key, { freeUnits, quantity });
      continue;
    }

    log('cap/remove:', item.key, { freeUnits, quantity, discounted, originalLineTotal });
    updates[item.key as string] = freeUnits; // 0 removes the line
  }

  return updates;
}
